/**
 * annotationUtils.js
 * ----------------------------------------------------------------------------
 * Fábricas dos elementos auxiliares do desenho que NÃO fazem parte da ficha
 * técnica financeira: elementos técnicos, anotações de texto e imagens.
 *
 * Todos usam coordenadas em PIXELS do desenho (x, y) — diferente das peças,
 * que usam percentual para manter compatibilidade com a ficha. Isso é
 * intencional: elementos/anotações/imagens são uma camada nova e livre.
 */

/** Gera um id único e estável o suficiente para keys/edição. */
export function uid(prefix = "el") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 7)}`;
}

/**
 * Catálogo dos tipos de elemento técnico suportados.
 * `shape` diz ao canvas como desenhar; `label` é o nome amigável.
 * PONTO DE EXTENSÃO: adicione um novo tipo aqui + um case no DrawingCanvas.
 */
export const ELEMENT_TYPES = [
  { type: "rect", label: "Retângulo técnico", shape: "rect" },
  { type: "line", label: "Linha", shape: "line" },
  { type: "arrow", label: "Seta", shape: "arrow" },
  { type: "hole", label: "Furo", shape: "circle" },
  { type: "cutout_rect", label: "Recorte retangular", shape: "rect" },
  { type: "cutout_circle", label: "Recorte circular", shape: "circle" },
  { type: "sink", label: "Área de cuba", shape: "rect" },
  { type: "cooktop", label: "Área de cooktop", shape: "rect" },
  { type: "seam", label: "Marcação de emenda", shape: "line" },
  { type: "wall", label: "Marcação de parede", shape: "line" },
  { type: "fronton", label: "Marcação de frontão", shape: "rect" },
  { type: "skirt", label: "Marcação de saia", shape: "rect" },
  { type: "note", label: "Observação livre", shape: "text" }
];

export function getElementMeta(type) {
  return ELEMENT_TYPES.find(e => e.type === type) || ELEMENT_TYPES[0];
}

/**
 * Cria um elemento técnico do desenho.
 * @param {string} type tipo (ver ELEMENT_TYPES)
 * @param {object} overrides posição/dimensões iniciais
 */
export function createDrawingElement(type = "rect", overrides = {}) {
  const meta = getElementMeta(type);
  return {
    id: uid("element"),
    type,
    shape: meta.shape,
    label: overrides.label || meta.label,
    x: overrides.x != null ? overrides.x : 80,
    y: overrides.y != null ? overrides.y : 80,
    width: overrides.width != null ? overrides.width : 60,
    height: overrides.height != null ? overrides.height : 40,
    rotation: overrides.rotation || 0,
    linkedPieceId: overrides.linkedPieceId || null,
    style: overrides.style || {},
    metadata: overrides.metadata || {}
  };
}

/** Cria uma anotação de texto. */
export function createAnnotation(overrides = {}) {
  return {
    id: uid("annotation"),
    text: overrides.text != null ? overrides.text : "Nova anotação",
    x: overrides.x != null ? overrides.x : 100,
    y: overrides.y != null ? overrides.y : 120,
    rotation: overrides.rotation || 0,
    fontSize: overrides.fontSize || 12,
    linkedPieceId: overrides.linkedPieceId || null,
    // linha de chamada (líder) pontilhada até um ponto do desenho
    // (estilo "• PIA", "• COOKTOP" das pranchas). null = sem líder.
    leader: overrides.leader || null
  };
}

/**
 * Cria uma imagem/referência do desenho.
 * src pode ser URL ou base64. O upload real (se houver) fica no componente pai.
 */
export function createDrawingImage(overrides = {}) {
  return {
    id: uid("image"),
    src: overrides.src || "",
    x: overrides.x != null ? overrides.x : 100,
    y: overrides.y != null ? overrides.y : 100,
    width: overrides.width != null ? overrides.width : 180,
    height: overrides.height != null ? overrides.height : 120,
    rotation: overrides.rotation || 0,
    opacity: overrides.opacity != null ? overrides.opacity : 1,
    locked: overrides.locked || false,
    label: overrides.label || "Referência"
  };
}
